/**
 * A pilot's milestones (PRD §5.4).
 *
 * The whole list saves at once, like the KPI table and for the same reason: a
 * reorder plus an add plus a status change is one intent, and the API replaces
 * the set.
 *
 * A late milestone is flagged **in words**, not by colour (PRD §9.7) — and
 * "late" here means past its due date and not yet done, because a milestone
 * completed after its date is history while an open overdue one is a task.
 *
 * The completion date is not editable. The server stamps it when the status
 * becomes `DONE`, so "done" and "when" cannot disagree; offering a field for it
 * would invite exactly that.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.2, PRD §5.4, §9.7
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PilotMilestone } from '@nexus/contracts';

import {
  MILESTONE_STATUSES,
  MILESTONE_TONE,
} from '../../../components/domain/pilot-status';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card, CardBody, CardHead } from '../../../components/ui/Card';
import { Empty } from '../../../components/ui/Empty';
import { Input } from '../../../components/ui/Input';
import { Kicker } from '../../../components/ui/Kicker';
import { Select } from '../../../components/ui/Select';
import { formatDate } from '../../../lib/format';

import type { MilestoneDraft } from './milestone-list';
import {
  countDone,
  emptyMilestoneDraft,
  isMilestoneLate,
  statusChangeHandler,
  toMilestoneDraft,
} from './milestone-list';

export interface MilestoneListProps {
  milestones: readonly PilotMilestone[];
  canEdit: boolean;
  onSave: (drafts: readonly MilestoneDraft[]) => void;
  isSaving: boolean;
}

const FIELD_LABEL =
  'mb-1 block font-display text-[10px] font-extrabold tracking-[0.1em] text-body uppercase';

export function MilestoneList({
  milestones,
  canEdit,
  onSave,
  isSaving,
}: MilestoneListProps): React.JSX.Element {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState<MilestoneDraft[] | null>(null);

  /* Null means "not editing". Deriving the drafts from props each render would
     discard a half-finished edit whenever the pilot refetched — which every
     mutation causes. */
  const editing = drafts !== null;

  const update = (
    current: readonly MilestoneDraft[],
    index: number,
    patch: Partial<MilestoneDraft>,
  ): void => {
    setDrafts(current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  return (
    <Card>
      <CardHead
        actions={
          canEdit ? (
            editing ? (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setDrafts(null);
                  }}
                >
                  {t('actions.cancel')}
                </Button>
                <Button
                  size="sm"
                  disabled={isSaving}
                  onClick={() => {
                    onSave(drafts);
                    setDrafts(null);
                  }}
                >
                  {isSaving ? t('pilot.milestones.saving') : t('pilot.milestones.save')}
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setDrafts(milestones.map(toMilestoneDraft));
                }}
              >
                {t('pilot.milestones.edit')}
              </Button>
            )
          ) : undefined
        }
      >
        <Kicker as="h2">
          {/* Done out of total, because that is the progress figure someone
              asks for — a bare count of milestones says nothing. */}
          {milestones.length === 0
            ? t('pilot.milestones.title')
            : t('pilot.milestones.titleWithCount', {
                done: countDone(milestones),
                total: milestones.length,
              })}
        </Kicker>
      </CardHead>

      <CardBody flush>
        {editing ? (
          <div className="p-s3">
            <ul className="gap-s3 flex flex-col">
              {drafts.map((draft, index) => (
                <li key={draft.key} className="gap-s2 grid grid-cols-1 md:grid-cols-3">
                  <label className="md:col-span-1">
                    <span className={FIELD_LABEL}>
                      {t('pilot.milestones.description')}
                    </span>
                    <Input
                      value={draft.description}
                      onChange={(event) => {
                        update(drafts, index, { description: event.target.value });
                      }}
                    />
                  </label>
                  <label>
                    <span className={FIELD_LABEL}>{t('pilot.milestones.due')}</span>
                    <Input
                      type="date"
                      value={draft.dueDate}
                      onChange={(event) => {
                        update(drafts, index, { dueDate: event.target.value });
                      }}
                    />
                  </label>
                  <label>
                    <span className={FIELD_LABEL}>{t('pilot.milestones.status')}</span>
                    <Select
                      value={draft.status}
                      /* v8 ignore next 3 -- a Radix Select cannot be driven to
                         invoke its handler in jsdom: it positions its viewport
                         with `document.elementFromPoint`, which jsdom does not
                         implement. The DECISION is `statusChangeHandler`, which
                         is tested directly; what remains here is the binding. */
                      onValueChange={statusChangeHandler((status) => {
                        update(drafts, index, { status });
                      })}
                      options={MILESTONE_STATUSES.map((status) => ({
                        value: status,
                        label: t(`pilot.milestoneStatus.${status}`),
                      }))}
                      ariaLabel={t('pilot.milestones.status')}
                    />
                  </label>
                </li>
              ))}
            </ul>

            <div className="gap-s2 mt-s3 flex">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setDrafts([...drafts, emptyMilestoneDraft()]);
                }}
              >
                {t('pilot.milestones.add')}
              </Button>
              {drafts.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setDrafts(drafts.slice(0, -1));
                  }}
                >
                  {t('pilot.milestones.removeLast')}
                </Button>
              )}
            </div>
          </div>
        ) : milestones.length === 0 ? (
          <Empty title={t('pilot.milestones.emptyTitle')}>
            {t('pilot.milestones.emptyBody')}
          </Empty>
        ) : (
          <ul>
            {milestones.map((milestone) => {
              const late = isMilestoneLate(milestone);

              return (
                <li
                  key={milestone.id}
                  className="border-gray-100 gap-s3 px-s3 flex items-center border-b py-2.5 last:border-b-0"
                >
                  <span className="text-body min-w-0 flex-1 text-[13px]">
                    {milestone.description}
                  </span>

                  <Badge tone={MILESTONE_TONE[milestone.status]}>
                    {t(`pilot.milestoneStatus.${milestone.status}`)}
                  </Badge>

                  {/* In words, not a red date: PRD §9.7. */}
                  {late ? <Badge tone="bad">{t('pilot.milestones.late')}</Badge> : null}

                  <span className="text-body w-[92px] flex-none text-right text-xs tabular-nums">
                    {milestone.dueDate === null ? (
                      <span className="text-gray-400">
                        {t('pilot.milestones.noDate')}
                      </span>
                    ) : (
                      formatDate(milestone.dueDate)
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
